import { TFunction } from 'i18next'

import { BankAccountSource } from '@features/bankAccounts/enums/bankAccountSource'
import { BankAccountStatus } from '@features/bankAccounts/enums/bankAccountStatus'
import { BankAccount } from '@features/bankAccounts/types/bankAccount'
import { TransactionType } from '@features/bankTransactions/enums/transactionType'
import { BillType } from '@features/bills/enums/billType'
import { PayableSubjectType } from '@features/paymentReconciliationModal/types/payableSubjectType'
import { BankLineStatus } from '@features/reconciliation/enums/bankLineStatus'
import { BookkeepingItemType } from '@features/reconciliation/enums/bookkeepingItemType'
import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { InvoiceType } from '@features/reconciliation/types/invoiceType'
import { Account, AccountNature } from '@modules-deprecated/app/accounts/types'
import {
  BankLine,
  BankLineMatch,
  Reconcilable,
  ReconciliationSuggestion,
  ReconciliationTransaction,
} from '@modules-deprecated/bankReconciliation/types'

import { Side } from '../../../../../../enums/side'
import { Currency } from '../../../../../../types/currency'

export const SANDBOX_BANK_ACCOUNT_ID = 'sandbox-bank-account'
export const SANDBOX_BOOKKEEPING_ACCOUNT_ID = 'sandbox-bookkeeping-account'

// Every market runs the sandbox in euros, so only the counterparties and labels are localized.
const SANDBOX_CURRENCY: Currency = 'EUR'

/**
 * Counterparties are seeded by role rather than by name so every market can show merchants and
 * customers a local user would recognise, while the shape of the list itself (amounts, dates,
 * which line carries a suggestion) stays identical everywhere.
 */
type PartyKey =
  | 'accountant'
  | 'ads'
  | 'artisanGoods'
  | 'bigBoxStore'
  | 'bookstore'
  | 'broadband'
  | 'cleaning'
  | 'construction'
  | 'consultancy'
  | 'courier'
  | 'creativeSoftware'
  | 'creativeStudio'
  | 'designStudio'
  | 'digitalAgency'
  | 'electronics'
  | 'energy'
  | 'fuelStation'
  | 'groceries'
  | 'gym'
  | 'hosting'
  | 'insurance'
  | 'landlord'
  | 'lawFirm'
  | 'officeSupplies'
  | 'pharmacy'
  | 'postal'
  | 'productivitySuite'
  | 'realEstate'
  | 'streaming'
  | 'telecom'

interface SandboxMarket {
  bankName: string
  accountName: string
  ownerName: string
  iban: string
  bic: string
  bankAccountNo: string
  bookkeepingAccountName: string
  /** Prefix a bank narration uses when the transfer references an outgoing invoice. */
  invoiceLabel: string
  officeRentLabel: string
  parties: Record<PartyKey, string>
}

type SandboxMarketLanguage = 'da' | 'de' | 'en' | 'fr' | 'nl'

const sandboxMarkets: Record<SandboxMarketLanguage, SandboxMarket> = {
  da: {
    bankName: 'Lunar Bank',
    accountName: 'Erhvervskonto',
    ownerName: 'Lindgren & Partnere ApS',
    iban: 'DK5000400440116243',
    bic: 'LUNADKK1',
    bankAccountNo: '40010440116243',
    bookkeepingAccountName: 'Erhvervskonto',
    invoiceLabel: 'Faktura',
    officeRentLabel: 'Kontorleje',
    parties: {
      accountant: 'Revisionsfirmaet Lund',
      ads: 'Google Ads',
      artisanGoods: 'Hjortholm Kunsthåndværk',
      bigBoxStore: 'Elgiganten',
      bookstore: 'Kapitel Boghandel',
      broadband: 'Stofa Erhverv',
      cleaning: 'Rengøringsservice Nord',
      construction: 'Mikkelsen Byg A/S',
      consultancy: 'Winther & Co',
      courier: 'GLS',
      creativeSoftware: 'Adobe Creative Cloud',
      creativeStudio: 'Bellman Kreativ Studie',
      designStudio: 'Rosendahl Designstudie',
      digitalAgency: 'Nova Digital Bureau',
      electronics: 'Power',
      energy: 'Ørsted',
      fuelStation: 'Circle K',
      groceries: 'Netto',
      gym: 'Fitness World',
      hosting: 'Nordvind Hosting',
      insurance: 'Tryg Erhvervsforsikring',
      landlord: 'Havnegården Ejendomme',
      lawFirm: 'Advokatfirmaet Mørch',
      officeSupplies: 'Lomax',
      pharmacy: 'Søndergade Apotek',
      postal: 'PostNord',
      productivitySuite: 'Microsoft 365 Business',
      realEstate: 'Færgegården Ejendomme',
      streaming: 'Netflix',
      telecom: 'TDC Erhverv',
    },
  },
  de: {
    bankName: 'Lumen Bank',
    accountName: 'Geschäftskonto',
    ownerName: 'Lehmann & Partner GmbH',
    iban: 'DE89370400440532013000',
    bic: 'LUMNDEFF',
    bankAccountNo: '0532013000',
    bookkeepingAccountName: 'Geschäftskonto',
    invoiceLabel: 'Rechnung',
    officeRentLabel: 'Büromiete',
    parties: {
      accountant: 'Steuerkanzlei Lehner',
      ads: 'Google Ads',
      artisanGoods: 'Dubois Kunsthandwerk',
      bigBoxStore: 'Saturn',
      bookstore: 'Buchhandlung Kapitel',
      broadband: 'Vodafone Business Internet',
      cleaning: 'GlanzRein Gebäudeservice',
      construction: 'Mertens Bau GmbH',
      consultancy: 'Wittfeld & Co',
      courier: 'DHL',
      creativeSoftware: 'Adobe Creative Cloud',
      creativeStudio: 'Bellmann Kreativstudio',
      designStudio: 'Rosenberg Designstudio',
      digitalAgency: 'Nova Digitalagentur',
      electronics: 'MediaMarkt',
      energy: 'E.ON',
      fuelStation: 'Aral',
      groceries: 'REWE',
      gym: 'FitX',
      hosting: 'Nordwind Hosting',
      insurance: 'Allianz Firmenversicherung',
      landlord: 'Hafenpark Immobilien',
      lawFirm: 'Kanzlei Moritz',
      officeSupplies: 'Staples',
      pharmacy: 'Gauting Apotheke',
      postal: 'Deutsche Post',
      productivitySuite: 'Microsoft 365 Business',
      realEstate: 'Ferring Immobilien',
      streaming: 'Netflix',
      telecom: 'Telekom Business',
    },
  },
  en: {
    bankName: 'Lumen Bank',
    accountName: 'Business Checking',
    ownerName: 'Lawson & Associates LLC',
    iban: 'US33LUMN0000001234567890',
    bic: 'LUMNUS33',
    bankAccountNo: '1234567890',
    bookkeepingAccountName: 'Business Account',
    invoiceLabel: 'Invoice',
    officeRentLabel: 'Office Rent',
    parties: {
      accountant: 'Whitmore Accounting',
      ads: 'Google Ads',
      artisanGoods: 'Dubois Artisan Goods',
      bigBoxStore: 'Best Buy',
      bookstore: 'Chapel Hill Bookstore',
      broadband: 'Comcast Business',
      cleaning: 'SparkleClean Services',
      construction: 'Meridian Construction Group',
      consultancy: 'Whitfield & Co',
      courier: 'FedEx',
      creativeSoftware: 'Adobe Creative Cloud',
      creativeStudio: 'Bellamy Creative Studio',
      designStudio: 'Rowan Design Studio',
      digitalAgency: 'Nova Digital Agency',
      electronics: 'B&H Photo',
      energy: 'Con Edison',
      fuelStation: 'Chevron',
      groceries: 'Whole Foods Market',
      gym: 'Planet Fitness',
      hosting: 'Northwind Hosting',
      insurance: 'State Farm Business',
      landlord: 'Harbour Estates',
      lawFirm: 'Morgan Legal Partners',
      officeSupplies: 'Staples',
      pharmacy: 'Garfield Pharmacy',
      postal: 'USPS',
      productivitySuite: 'Microsoft 365 Business',
      realEstate: 'Fairfield Real Estate',
      streaming: 'Netflix',
      telecom: 'Verizon Business',
    },
  },
  fr: {
    bankName: 'Lumen Bank',
    accountName: 'Compte Pro',
    ownerName: 'Lefèvre & Associés SARL',
    iban: 'FR7630006000011234567890189',
    bic: 'LUMNFRPP',
    bankAccountNo: '30006000011234567890189',
    bookkeepingAccountName: 'Compte professionnel',
    invoiceLabel: 'Facture',
    officeRentLabel: 'Loyer bureau',
    parties: {
      accountant: 'Cabinet Comptable Lefort',
      ads: 'Google Ads',
      artisanGoods: 'Dubois Artisanat',
      bigBoxStore: 'Darty',
      bookstore: 'Librairie Chapelle',
      broadband: 'SFR Business',
      cleaning: 'Net Éclat Services',
      construction: 'Mercier Construction',
      consultancy: 'Whitfield & Cie',
      courier: 'Chronopost',
      creativeSoftware: 'Adobe Creative Cloud',
      creativeStudio: 'Studio Créatif Bellamy',
      designStudio: 'Studio Rousseau',
      digitalAgency: 'Agence Nova Digital',
      electronics: 'Fnac',
      energy: 'EDF',
      fuelStation: 'TotalEnergies',
      groceries: 'Carrefour',
      gym: 'Basic-Fit',
      hosting: 'Northwind Hébergement',
      insurance: 'AXA Assurance Pro',
      landlord: 'Havre Immobilier',
      lawFirm: 'Cabinet Moreau',
      officeSupplies: 'Staples',
      pharmacy: 'Pharmacie Gauthier',
      postal: 'La Poste',
      productivitySuite: 'Microsoft 365 Business',
      realEstate: 'Ferrand Immobilier',
      streaming: 'Netflix',
      telecom: 'Orange Business',
    },
  },
  nl: {
    bankName: 'Lumen Bank',
    accountName: 'Zakelijke rekening',
    ownerName: 'Vermeer & Partners B.V.',
    iban: 'NL91LUMN0417164300',
    bic: 'LUMNNL2A',
    bankAccountNo: '0417164300',
    bookkeepingAccountName: 'Zakelijke rekening',
    invoiceLabel: 'Factuur',
    officeRentLabel: 'Kantoorhuur',
    parties: {
      accountant: 'Accountantskantoor Leeuwen',
      ads: 'Google Ads',
      artisanGoods: 'Dubois Ambachtelijke Waren',
      bigBoxStore: 'MediaMarkt',
      bookstore: 'Boekhandel Kapittel',
      broadband: 'Ziggo Zakelijk',
      cleaning: 'GlansRein Schoonmaak',
      construction: 'Meerman Bouwgroep',
      consultancy: 'Witveld & Co',
      courier: 'DHL',
      creativeSoftware: 'Adobe Creative Cloud',
      creativeStudio: 'Bellamy Creatief Studio',
      designStudio: 'Studio Roosendaal',
      digitalAgency: 'Nova Digitaal Bureau',
      electronics: 'Coolblue',
      energy: 'Eneco',
      fuelStation: 'Shell',
      groceries: 'Albert Heijn',
      gym: 'Basic-Fit',
      hosting: 'Noordwind Hosting',
      insurance: 'Centraal Beheer Zakelijk',
      landlord: 'Havenkwartier Vastgoed',
      lawFirm: 'Advocatenkantoor Mooren',
      officeSupplies: 'Staples',
      pharmacy: 'Apotheek Zonnehof',
      postal: 'PostNL',
      productivitySuite: 'Microsoft 365 Business',
      realEstate: 'Ferwerda Vastgoed',
      streaming: 'Netflix',
      telecom: 'KPN Zakelijk',
    },
  },
}

const FALLBACK_MARKET_LANGUAGE: SandboxMarketLanguage = 'en'

const isSandboxMarketLanguage = (language: string): language is SandboxMarketLanguage => language in sandboxMarkets

const resolveMarket = (language: string): SandboxMarket => {
  const [baseLanguage] = language.split('-')

  return sandboxMarkets[isSandboxMarketLanguage(baseLanguage) ? baseLanguage : FALLBACK_MARKET_LANGUAGE]
}

// The most recent seeded transaction is "today" from the sandbox's point of view; every other
// seeded date is expressed as an offset from it so the list keeps looking recent regardless of
// when the sandbox is actually opened.
const SANDBOX_TODAY = new Date('2026-09-22T00:00:00Z')

const dateDaysAgo = (daysAgo: number): string => {
  const date = new Date(SANDBOX_TODAY)
  date.setUTCDate(date.getUTCDate() - daysAgo)
  return date.toISOString().slice(0, 10)
}

const buildBankLine = (
  overrides: Partial<BankLine> & Pick<BankLine, 'id' | 'entryDate' | 'description' | 'amount' | 'side'>,
): BankLine => ({
  groupId: overrides.id,
  matchId: '',
  balance: 0,
  type: TransactionType.Card,
  accountId: SANDBOX_BOOKKEEPING_ACCOUNT_ID,
  contraAccountId: '',
  receiptState: null,
  provider: 'manual',
  externalId: null,
  status: BankLineStatus.Booked,
  ...overrides,
})

type ItemKind = 'bill' | 'invoice' | 'externalInvoice'

interface ItemSeed {
  id: string
  kind: ItemKind
  number: string
  party: PartyKey
  daysAgo: number
}

/** Items a user can pick manually carry their own amount instead of inheriting a bank line's. */
interface OpenItemSeed extends ItemSeed {
  amount: number
}

/** A bank narration is either the bare counterparty or the counterparty plus a localized suffix. */
type Narration = { kind: 'party' } | { kind: 'invoice'; number: string } | { kind: 'officeRent' }

interface BankLineSeed {
  daysAgo: number
  party: PartyKey
  narration: Narration
  amount: number
  side: Side
  type: TransactionType
}

/**
 * A transaction the matching service has proposed an item for. The item inherits the bank line's
 * own amount rather than carrying a second hardcoded number, so a suggestion is always an exact
 * match: any difference at all makes the drawer render the difference selector on a suggestion
 * the user hasn't even accepted yet.
 */
interface SuggestedSeed extends BankLineSeed {
  item: ItemSeed
}

/** A transaction that is already reconciled: its matched item lives only in the match, not in the open pool. */
interface ReconciledSeed extends BankLineSeed {
  item: ItemSeed
}

// The two most recent transactions, both with a suggestion, so the sandbox opens on the
// "review a suggestion" flow before anything else.
const leadingSuggestedSeeds: SuggestedSeed[] = [
  {
    daysAgo: 1,
    party: 'officeSupplies',
    narration: { kind: 'party' },
    amount: 142.8,
    side: Side.Credit,
    type: TransactionType.Card,
    item: { id: 'sandbox-bill-office-supplies', kind: 'bill', number: '118', party: 'officeSupplies', daysAgo: 3 },
  },
  {
    daysAgo: 1,
    party: 'construction',
    narration: { kind: 'invoice', number: '212' },
    amount: 9600,
    side: Side.Debit,
    type: TransactionType.Transfer,
    item: { id: 'sandbox-invoice-construction', kind: 'invoice', number: '212', party: 'construction', daysAgo: 12 },
  },
]

// Everything below the two leading suggestions: suggestions interleaved with transactions that
// have nothing proposed yet, so both states are visible without scrolling past a block of one kind.
const suggestedSeeds: SuggestedSeed[] = [
  {
    daysAgo: 3,
    party: 'accountant',
    narration: { kind: 'party' },
    amount: 280,
    side: Side.Credit,
    type: TransactionType.Transfer,
    item: { id: 'sandbox-bill-accountant', kind: 'bill', number: '119', party: 'accountant', daysAgo: 6 },
  },
  {
    daysAgo: 5,
    party: 'designStudio',
    narration: { kind: 'invoice', number: '207' },
    amount: 1850,
    side: Side.Debit,
    type: TransactionType.Transfer,
    item: { id: 'sandbox-invoice-design-studio', kind: 'invoice', number: '207', party: 'designStudio', daysAgo: 16 },
  },
  {
    daysAgo: 6,
    party: 'ads',
    narration: { kind: 'party' },
    amount: 320,
    side: Side.Credit,
    type: TransactionType.Card,
    item: { id: 'sandbox-bill-ads', kind: 'bill', number: '121', party: 'ads', daysAgo: 7 },
  },
  {
    daysAgo: 8,
    party: 'energy',
    narration: { kind: 'party' },
    amount: 95,
    side: Side.Credit,
    type: TransactionType.DirectDebit,
    item: { id: 'sandbox-bill-energy', kind: 'bill', number: '124', party: 'energy', daysAgo: 11 },
  },
  {
    daysAgo: 9,
    party: 'landlord',
    narration: { kind: 'officeRent' },
    amount: 1450,
    side: Side.Credit,
    type: TransactionType.Transfer,
    item: { id: 'sandbox-bill-landlord', kind: 'bill', number: '126', party: 'landlord', daysAgo: 13 },
  },
  {
    daysAgo: 11,
    party: 'digitalAgency',
    narration: { kind: 'invoice', number: '189' },
    amount: 3400,
    side: Side.Debit,
    type: TransactionType.Transfer,
    item: {
      id: 'sandbox-external-invoice-digital-agency',
      kind: 'externalInvoice',
      number: '189',
      party: 'digitalAgency',
      daysAgo: 22,
    },
  },
  {
    daysAgo: 14,
    party: 'courier',
    narration: { kind: 'party' },
    amount: 15.9,
    side: Side.Credit,
    type: TransactionType.Card,
    item: { id: 'sandbox-bill-courier', kind: 'bill', number: '128', party: 'courier', daysAgo: 17 },
  },
  {
    daysAgo: 16,
    party: 'electronics',
    narration: { kind: 'party' },
    amount: 349,
    side: Side.Credit,
    type: TransactionType.Card,
    item: { id: 'sandbox-bill-electronics', kind: 'bill', number: '130', party: 'electronics', daysAgo: 18 },
  },
]

// No suggestion attached — these are the ones a user has to match by hand from the items list.
const toMatchSeeds: BankLineSeed[] = [
  {
    daysAgo: 3,
    party: 'bigBoxStore',
    narration: { kind: 'party' },
    amount: 899,
    side: Side.Credit,
    type: TransactionType.Card,
  },
  {
    daysAgo: 6,
    party: 'fuelStation',
    narration: { kind: 'party' },
    amount: 58.4,
    side: Side.Credit,
    type: TransactionType.Card,
  },
  {
    daysAgo: 9,
    party: 'bookstore',
    narration: { kind: 'invoice', number: '199' },
    amount: 620,
    side: Side.Debit,
    type: TransactionType.Transfer,
  },
  {
    daysAgo: 12,
    party: 'creativeSoftware',
    narration: { kind: 'party' },
    amount: 79.99,
    side: Side.Credit,
    type: TransactionType.Card,
  },
  {
    daysAgo: 14,
    party: 'pharmacy',
    narration: { kind: 'invoice', number: '188' },
    amount: 2900,
    side: Side.Debit,
    type: TransactionType.Transfer,
  },
]

// Near misses for toMatchSeeds — each is a few units off the transaction it belongs to, so the item
// is obviously the right one but can't be auto-matched on amount, plus a couple of still-open
// items with no counterpart at all.
const openItemSeeds: OpenItemSeed[] = [
  { id: 'sandbox-bill-big-box-store', kind: 'bill', number: '131', party: 'bigBoxStore', amount: 903.5, daysAgo: 5 },
  { id: 'sandbox-bill-fuel-station', kind: 'bill', number: '132', party: 'fuelStation', amount: 62.3, daysAgo: 8 },
  { id: 'sandbox-invoice-bookstore', kind: 'invoice', number: '199', party: 'bookstore', amount: 624.5, daysAgo: 19 },
  {
    id: 'sandbox-bill-creative-software',
    kind: 'bill',
    number: '133',
    party: 'creativeSoftware',
    amount: 84.2,
    daysAgo: 15,
  },
  { id: 'sandbox-invoice-pharmacy', kind: 'invoice', number: '188', party: 'pharmacy', amount: 2896.5, daysAgo: 24 },
  { id: 'sandbox-bill-cleaning', kind: 'bill', number: '135', party: 'cleaning', amount: 45, daysAgo: 7 },
  {
    id: 'sandbox-invoice-artisan-goods',
    kind: 'invoice',
    number: '205',
    party: 'artisanGoods',
    amount: 650,
    daysAgo: 13,
  },
]

// Older transactions that have already been reconciled. They sort to the bottom of the list
// because the list is ordered by entry date descending.
const reconciledSeeds: ReconciledSeed[] = [
  {
    daysAgo: 20,
    party: 'telecom',
    narration: { kind: 'party' },
    amount: 35,
    side: Side.Credit,
    type: TransactionType.DirectDebit,
    item: { id: 'sandbox-bill-telecom', kind: 'bill', number: '101', party: 'telecom', daysAgo: 22 },
  },
  {
    daysAgo: 20,
    party: 'realEstate',
    narration: { kind: 'invoice', number: '171' },
    amount: 4200,
    side: Side.Debit,
    type: TransactionType.Transfer,
    item: { id: 'sandbox-invoice-real-estate', kind: 'invoice', number: '171', party: 'realEstate', daysAgo: 31 },
  },
  {
    daysAgo: 22,
    party: 'groceries',
    narration: { kind: 'party' },
    amount: 42.3,
    side: Side.Credit,
    type: TransactionType.Card,
    item: { id: 'sandbox-bill-groceries', kind: 'bill', number: '102', party: 'groceries', daysAgo: 22 },
  },
  {
    daysAgo: 24,
    party: 'insurance',
    narration: { kind: 'party' },
    amount: 55,
    side: Side.Credit,
    type: TransactionType.DirectDebit,
    item: { id: 'sandbox-bill-insurance', kind: 'bill', number: '103', party: 'insurance', daysAgo: 26 },
  },
  {
    daysAgo: 24,
    party: 'productivitySuite',
    narration: { kind: 'party' },
    amount: 99,
    side: Side.Credit,
    type: TransactionType.Card,
    item: {
      id: 'sandbox-bill-productivity-suite',
      kind: 'bill',
      number: '104',
      party: 'productivitySuite',
      daysAgo: 25,
    },
  },
  {
    daysAgo: 26,
    party: 'broadband',
    narration: { kind: 'party' },
    amount: 42,
    side: Side.Credit,
    type: TransactionType.DirectDebit,
    item: { id: 'sandbox-bill-broadband', kind: 'bill', number: '105', party: 'broadband', daysAgo: 28 },
  },
  {
    daysAgo: 28,
    party: 'landlord',
    narration: { kind: 'officeRent' },
    amount: 1450,
    side: Side.Credit,
    type: TransactionType.Transfer,
    item: { id: 'sandbox-bill-landlord-previous', kind: 'bill', number: '106', party: 'landlord', daysAgo: 32 },
  },
  {
    daysAgo: 31,
    party: 'creativeStudio',
    narration: { kind: 'invoice', number: '143' },
    amount: 3800,
    side: Side.Debit,
    type: TransactionType.Transfer,
    item: {
      id: 'sandbox-invoice-creative-studio',
      kind: 'invoice',
      number: '143',
      party: 'creativeStudio',
      daysAgo: 42,
    },
  },
  {
    daysAgo: 31,
    party: 'streaming',
    narration: { kind: 'party' },
    amount: 13.49,
    side: Side.Credit,
    type: TransactionType.Card,
    item: { id: 'sandbox-bill-streaming', kind: 'bill', number: '107', party: 'streaming', daysAgo: 31 },
  },
  {
    daysAgo: 34,
    party: 'lawFirm',
    narration: { kind: 'party' },
    amount: 650,
    side: Side.Credit,
    type: TransactionType.Transfer,
    item: { id: 'sandbox-bill-law-firm', kind: 'bill', number: '108', party: 'lawFirm', daysAgo: 38 },
  },
  {
    daysAgo: 37,
    party: 'hosting',
    narration: { kind: 'party' },
    amount: 39.99,
    side: Side.Credit,
    type: TransactionType.Card,
    item: { id: 'sandbox-bill-hosting', kind: 'bill', number: '109', party: 'hosting', daysAgo: 37 },
  },
  {
    daysAgo: 40,
    party: 'consultancy',
    narration: { kind: 'invoice', number: '121' },
    amount: 1200,
    side: Side.Debit,
    type: TransactionType.Transfer,
    item: { id: 'sandbox-invoice-consultancy', kind: 'invoice', number: '121', party: 'consultancy', daysAgo: 51 },
  },
  {
    daysAgo: 40,
    party: 'gym',
    narration: { kind: 'party' },
    amount: 29.99,
    side: Side.Credit,
    type: TransactionType.DirectDebit,
    item: { id: 'sandbox-bill-gym', kind: 'bill', number: '110', party: 'gym', daysAgo: 40 },
  },
  {
    daysAgo: 44,
    party: 'fuelStation',
    narration: { kind: 'party' },
    amount: 51.2,
    side: Side.Credit,
    type: TransactionType.Card,
    item: { id: 'sandbox-bill-fuel-station-older', kind: 'bill', number: '111', party: 'fuelStation', daysAgo: 44 },
  },
  {
    daysAgo: 48,
    party: 'postal',
    narration: { kind: 'party' },
    amount: 8.5,
    side: Side.Credit,
    type: TransactionType.Card,
    item: { id: 'sandbox-bill-postal', kind: 'bill', number: '112', party: 'postal', daysAgo: 48 },
  },
]

const bookkeepingItemTypeByKind: Record<ItemKind, BookkeepingItemType> = {
  bill: BookkeepingItemType.Bill,
  invoice: BookkeepingItemType.Invoice,
  externalInvoice: BookkeepingItemType.ExternalInvoice,
}

const payableSubjectTypeByBookkeepingItemType: Record<
  BookkeepingItemType.Bill | BookkeepingItemType.Invoice | BookkeepingItemType.ExternalInvoice,
  PayableSubjectType
> = {
  [BookkeepingItemType.Bill]: BillType.Bill,
  [BookkeepingItemType.Invoice]: InvoiceType.Invoice,
  [BookkeepingItemType.ExternalInvoice]: InvoiceType.ExternalInvoice,
}

export interface SandboxSeedData {
  accounts: Account[]
  bankAccounts: BankAccount[]
  bankLines: BankLine[]
  bookkeepingItems: BookkeepingItem[]
  suggestions: ReconciliationSuggestion[]
  matchedItemsByMatchId: Record<string, BookkeepingItem[]>
  currency: Currency
}

const buildSeedData = (market: SandboxMarket, t: TFunction): SandboxSeedData => {
  const partyName = (party: PartyKey): string => market.parties[party]

  const narrate = (party: PartyKey, narration: Narration): string => {
    switch (narration.kind) {
      case 'invoice':
        return `${partyName(party)} - ${market.invoiceLabel} INV-2026-0${narration.number}`
      case 'officeRent':
        return `${partyName(party)} - ${market.officeRentLabel}`
      default:
        return partyName(party)
    }
  }

  const bankAccount: BankAccount = {
    id: SANDBOX_BANK_ACCOUNT_ID,
    bookkeepingAccountId: SANDBOX_BOOKKEEPING_ACCOUNT_ID,
    bankName: market.bankName,
    accountName: market.accountName,
    customAccountName: null,
    ownerName: market.ownerName,
    type: 'checking',
    iban: market.iban,
    bic: market.bic,
    currency: SANDBOX_CURRENCY,
    availableBalance: { value: 42350.5, currency: SANDBOX_CURRENCY },
    bookedBalance: { value: 42350.5, currency: SANDBOX_CURRENCY },
    status: BankAccountStatus.OPEN,
    source: BankAccountSource.SHINE_BANKING,
    reconciliationStartDate: null,
  }

  // The bookkeeping-side counterpart of the bank account — needed wherever a component reads the
  // account list via useFetchAccounts (e.g. BankAccountSelect) rather than useFetchBankAccounts.
  const bookkeepingAccount: Account = {
    bankAccountNo: market.bankAccountNo,
    bankName: market.bankName,
    currencyId: SANDBOX_CURRENCY,
    description: '',
    id: SANDBOX_BOOKKEEPING_ACCOUNT_ID,
    isArchived: false,
    isBankAccount: true,
    isPaymentEnabled: false,
    name: market.bookkeepingAccountName,
    natureId: AccountNature.Asset,
    publicAccountId: SANDBOX_BOOKKEEPING_ACCOUNT_ID,
  }

  // Descriptions go through the same keys mapBill/mapInvoice/mapExternalInvoice use for real items,
  // and side follows the same convention as bank lines: Debit is money in, Credit is money out.
  const buildItem = (seed: ItemSeed, amount: number): BookkeepingItem => {
    const isBill = seed.kind === 'bill'

    return {
      id: seed.id,
      type: bookkeepingItemTypeByKind[seed.kind],
      amount,
      date: dateDaysAgo(seed.daysAgo),
      description: t(
        isBill ? 'bank_transactions.bookkeeping_item.purchase' : 'bank_transactions.bookkeeping_item.invoice',
        { number: seed.number, contact: partyName(seed.party) },
      ),
      currency: SANDBOX_CURRENCY,
      side: isBill ? Side.Credit : Side.Debit,
      ...(seed.kind === 'externalInvoice' ? { externalId: seed.id } : {}),
    }
  }

  const buildSeededBankLine = (seed: BankLineSeed, id: string): BankLine =>
    buildBankLine({
      id,
      entryDate: dateDaysAgo(seed.daysAgo),
      description: narrate(seed.party, seed.narration),
      amount: seed.amount,
      side: seed.side,
      type: seed.type,
    })

  /**
   * Builds a ReconciliationSuggestion pairing one seeded bank line with one seeded bookkeeping
   * item, mirroring the real matching-service response shape (snake_case, denormalized) rather
   * than referencing the two by id — see the plan's note on this being a hand-authored, not
   * computed, suggestion.
   */
  const buildSuggestion = (bankLine: BankLine, seed: ItemSeed, item: BookkeepingItem): ReconciliationSuggestion => {
    const transaction: ReconciliationTransaction = {
      id: bankLine.id,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      match_id: bankLine.matchId,
      amount: String(bankLine.amount),
      side: bankLine.side,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      bank_date: bankLine.entryDate,
      description: bankLine.description,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      account_id: bankLine.accountId,
      currency: SANDBOX_CURRENCY,
    }

    const reconcilable: Reconcilable = {
      id: `${item.type}:${item.id}`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      organization_id: '',
      type: payableSubjectTypeByBookkeepingItemType[
        item.type as BookkeepingItemType.Bill | BookkeepingItemType.Invoice | BookkeepingItemType.ExternalInvoice
      ],
      kind: item.type,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      entry_date: item.date,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      due_date: null,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      invoice_number: seed.number,
      state: 'approved',
      amount: item.amount,
      currency: SANDBOX_CURRENCY,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      is_paid: false,
      description: item.description,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      created_time: new Date(item.date),
      contact: { name: partyName(seed.party) },
    }

    return {
      id: `sandbox-suggestion-${bankLine.id}`,
      transactions: [transaction],
      reconcilables: [reconcilable],
    }
  }

  const suggestedEntries = [...leadingSuggestedSeeds, ...suggestedSeeds].map((seed, index) => {
    const bankLine = buildSeededBankLine(seed, `sandbox-bank-line-suggested-${index + 1}`)
    const item = buildItem(seed.item, bankLine.amount)

    return { bankLine, item, suggestion: buildSuggestion(bankLine, seed.item, item) }
  })

  const toMatchBankLines = toMatchSeeds.map((seed, index) =>
    buildSeededBankLine(seed, `sandbox-bank-line-to-match-${index + 1}`),
  )

  const reconciledEntries = reconciledSeeds.map((seed, index) => {
    const bankLine = buildSeededBankLine(seed, `sandbox-bank-line-reconciled-${index + 1}`)
    const match: BankLineMatch = {
      id: `sandbox-match-${bankLine.id}`,
      accountId: bankLine.accountId,
      entryDate: bankLine.entryDate,
      amount: bankLine.amount,
      side: bankLine.side,
      feeAccountId: null,
      isApproved: true,
      approvedTime: `${bankLine.entryDate}T09:00:00.000Z`,
      lines: [bankLine],
      lineIds: [bankLine.id],
      subjectAssociationIds: [],
      description: bankLine.description,
      currencyId: SANDBOX_CURRENCY,
    }

    return {
      bankLine: { ...bankLine, matchId: match.id, match },
      matchedItems: [buildItem(seed.item, bankLine.amount)],
    }
  })

  return {
    accounts: [bookkeepingAccount],
    bankAccounts: [bankAccount],
    bankLines: [
      ...suggestedEntries.map(({ bankLine }) => bankLine),
      ...toMatchBankLines,
      ...reconciledEntries.map(({ bankLine }) => bankLine),
    ],
    // Only unreconciled work is in the open pool: the items behind each suggestion plus the ones a
    // user can pick manually. Items belonging to an already-approved match are reachable through
    // matchedItemsByMatchId instead, the way a settled bill drops out of the matching list.
    bookkeepingItems: [
      ...suggestedEntries.map(({ item }) => item),
      ...openItemSeeds.map((seed) => buildItem(seed, seed.amount)),
    ],
    suggestions: suggestedEntries.map(({ suggestion }) => suggestion),
    matchedItemsByMatchId: Object.fromEntries(
      reconciledEntries.map(({ bankLine, matchedItems }) => [bankLine.matchId, matchedItems]),
    ),
    currency: SANDBOX_CURRENCY,
  }
}

export const buildSandboxSeedData = (language: string, t: TFunction): SandboxSeedData =>
  buildSeedData(resolveMarket(language), t)
